import React, { useState } from 'react';

const BecomeMerchant = () => {
    const [activeTab, setActiveTab] = useState('benefits');
    const [formData, setFormData] = useState({
        businessName: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        businessType: '',
        experience: '',
        message: ''
    });
    const [formStep, setFormStep] = useState(1);
    const [showSuccess, setShowSuccess] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // submit the form data to your backend
        setShowSuccess(true);
    };

    const handleNextStep = () => {
        setFormStep(prev => prev + 1);
    };

    const handlePrevStep = () => {
        setFormStep(prev => prev - 1);
    };

    const benefits = [
        {
            title: "Smart Financial Management",
            icon: "💰",
            description: "Seamless Accounting Integration: Syncs effortlessly with Tally, QuickBooks, and other tools."
        },
        {
            title: "Advanced Payment Solutions",
            icon: "🎓",
            description: "Multi-Payment Acceptance: Supports UPI, QR, Tap-to-Pay (NFC), and offline payments."
        },
        {
            title: "Business Growth & Customization",
            icon: "📈",
            description: "Multi-User Access & Role-Based Permissions: Allows secure employee access to financial records."
        },
        {
            title: "Reliable & Scalable Partnership",
            icon: "🖥️",
            description: "Trusted & Secure Platform: Built with industry-standard security for data protection."
        }
    ];

    const tiers = [
        {
            name: "Authorized Reseller",
            description: "Entry-level partnership for businesses new to our products",
            commission: "15-20%",
            requirements: "Valid business license, online presence, sales experience",
            benefits: [
                "Basic partner portal access",
                "Standard marketing materials",
                "Online product training",
                "Email support"
            ]
        },
        {
            name: "Silver Partner",
            description: "Mid-tier partnership with proven sales history",
            commission: "20-25%",
            requirements: "6+ months as Authorized Reseller, $10k+ quarterly sales",
            benefits: [
                "Enhanced partner portal features",
                "Co-branded marketing materials",
                "Virtual training sessions",
                "Priority email and phone support"
            ]
        },
        {
            name: "Gold Partner",
            description: "Premium partnership for high-volume sellers",
            commission: "25-30%",
            requirements: "12+ months as Silver Partner, $30k+ quarterly sales",
            benefits: [
                "Full partner portal access",
                "Custom marketing campaigns",
                "In-person training opportunities",
                "Dedicated account manager"
            ]
        }
    ];

    const processSteps = [
        {
            title: "Submit Application",
            description: "Complete our merchant application form with your business details"
        },
        {
            title: "Initial Assessment",
            description: "Our partnership team reviews your application and market potential"
        },
        {
            title: "Partnership Discussion",
            description: "We'll schedule a call to discuss opportunities and expectations"
        },
        {
            title: "Training & Onboarding",
            description: "Complete our comprehensive product and sales training"
        },
        {
            title: "Launch Partnership",
            description: "Receive your merchant credentials and start selling"
        }
    ];

    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section */}
            <div className="bg-primary mt-[90px] text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-4xl font-bold mb-4">Merchant <span className='text-secondary mx-2'>X</span> Partner</h1>
                    <p className="text-xl mb-8 max-w-xl mx-auto">
                        Join our smart calculator network and unlock new buisness opportunities
                    </p>
                    <button
                        className="bg-white text-secondary px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition duration-300"
                        onClick={() => {
                            setActiveTab('application');
                            window.scrollTo({
                                top: document.getElementById('content-tabs').offsetTop - 100,
                                behavior: 'smooth'
                            });
                        }}
                    >
                        Apply Now
                    </button>
                </div>
            </div>

            {/* Main Content Tabs */}
            <div id="content-tabs" className="container mx-auto py-12 px-4">
                <div className="flex justify-center mb-8 border-b border-gray-200 overflow-x-auto">
                    <button
                        className={`px-4 py-3 mx-2 font-medium text-lg border-b-2 ${activeTab === 'benefits' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'
                            } transition duration-300`}
                        onClick={() => setActiveTab('benefits')}
                    >
                        Benefits
                    </button>
                    <button
                        className={`px-4 py-3 mx-2 font-medium text-lg border-b-2 ${activeTab === 'application' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'
                            } transition duration-300`}
                        onClick={() => setActiveTab('application')}
                    >
                        Apply
                    </button>
                </div>

                {/* Content for each tab */}
                {/* Benefits Content */}
                {activeTab === 'benefits' && (
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-12">Why Partner a HISAAB?</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            {benefits.map((benefit, index) => (
                                <div
                                    key={index}
                                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 border border-gray-100"
                                >
                                    <div className="text-4xl mb-4">{benefit.icon}</div>
                                    <h3 className="text-xl font-bold mb-2 text-primary">{benefit.title}</h3>
                                    <p className="text-gray-600">{benefit.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Application Form continued */}
                {activeTab === 'application' && (
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-4">Apply to Become a Partner</h2>
                        <p className="text-center text-gray-600 mb-8">
                            Fill out the form below to start your application process
                        </p>

                        {!showSuccess ? (
                            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                                {/* Progress Indicator */}
                                <div className="mb-8">
                                    <div className="flex justify-between mb-2">
                                        {['Business Information', 'Experience', 'Submission'].map((label, idx) => (
                                            <div key={idx} className={`text-sm font-medium ${formStep > idx ? 'text-secondary' : 'text-gray-500'
                                                }`}>
                                                {label}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-secondary h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${(formStep / 3) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    {/* Step 1: Business Information */}
                                    {formStep === 1 && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Business Name</label>
                                                <input
                                                    type="text"
                                                    name="businessName"
                                                    value={formData.businessName}
                                                    onChange={handleInputChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Contact Name</label>
                                                <input
                                                    type="text"
                                                    name="contactName"
                                                    value={formData.contactName}
                                                    onChange={handleInputChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                    required
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-gray-700 font-medium mb-2">Email</label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-gray-700 font-medium mb-2">Phone</label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Website</label>
                                                <input
                                                    type="url"
                                                    name="website"
                                                    value={formData.website}
                                                    onChange={handleInputChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Business Type</label>
                                                <select
                                                    name="businessType"
                                                    value={formData.businessType}
                                                    onChange={handleInputChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                    required
                                                >
                                                    <option value="">Select Business Type</option>
                                                    <option value="retail">Retail Store</option>
                                                    <option value="ecommerce">E-commerce</option>
                                                    <option value="accounting">Accounting Firm</option>
                                                    <option value="consulting">Business Consulting</option>
                                                    <option value="technology">Technology Provider</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2: Experience */}
                                    {formStep === 2 && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">
                                                    Years of experience selling similar products
                                                </label>
                                                <select
                                                    name="experience"
                                                    value={formData.experience}
                                                    onChange={handleInputChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                    required
                                                >
                                                    <option value="">Select Experience</option>
                                                    <option value="0-1">Less than 1 year</option>
                                                    <option value="1-3">1-3 years</option>
                                                    <option value="3-5">3-5 years</option>
                                                    <option value="5+">5+ years</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">
                                                    Why are you interested in becoming a merchant partner?
                                                </label>
                                                <textarea
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    rows="5"
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                                                    required
                                                ></textarea>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3: Submission */}
                                    {formStep === 3 && (
                                        <div className="space-y-6">
                                            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                                                <h3 className="font-semibold text-indigo-800 mb-2">Almost there!</h3>
                                                <p className="text-indigo-700 text-sm">
                                                    Please review your information before submitting. Our partnership team will
                                                    contact you within 2-3 business days to discuss next steps.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                                                <div>
                                                    <span className="text-sm text-gray-500">Business Name</span>
                                                    <p className="font-medium">{formData.businessName || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Contact Name</span>
                                                    <p className="font-medium">{formData.contactName || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Email</span>
                                                    <p className="font-medium">{formData.email || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Phone</span>
                                                    <p className="font-medium">{formData.phone || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Website</span>
                                                    <p className="font-medium">{formData.website || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Business Type</span>
                                                    <p className="font-medium">{formData.businessType || "—"}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Experience</span>
                                                    <p className="font-medium">{formData.experience || "—"}</p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <span className="text-sm text-gray-500">Why interested</span>
                                                <p className="text-gray-700">{formData.message || "—"}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Navigation buttons */}
                                    <div className="flex justify-between mt-8">
                                        {formStep > 1 && (
                                            <button
                                                type="button"
                                                onClick={handlePrevStep}
                                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-300"
                                            >
                                                Back
                                            </button>
                                        )}

                                        {formStep < 3 ? (
                                            <button
                                                type="button"
                                                onClick={handleNextStep}
                                                className="ml-auto px-6 py-2 bg-secondary text-white rounded-lg transition duration-300"
                                            >
                                                Next
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="ml-auto px-6 py-2 bg-seconday text-white rounded-lg transition duration-300"
                                            >
                                                Submit Application
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-green-500 text-2xl">✓</span>
                                </div>
                                <h3 className="text-2xl font-bold mb-2">Application Submitted!</h3>
                                <p className="text-gray-600 mb-6">
                                    Thank you for your interest in becoming a merchant partner.
                                    Our team will review your application and contact you within 2-3 business days.
                                </p>
                                <button
                                    onClick={() => setActiveTab('benefits')}
                                    className="px-6 py-2 bg-secondary text-white rounded-lg transition duration-300"
                                >
                                    Learn More About Partnership Benefits
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer CTA */}
            <div className="bg-gray-50 py-12 border-t border-gray-200">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-2xl font-bold mb-4">Questions About Our Product?</h2>
                    <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                        Our partnership team is here to help. Contact us for more information about
                        becoming a merchant partner.
                    </p>
                    <div className="inline-flex items-center justify-center bg-purple-100 text-primary rounded-full px-4 py-2 font-medium">
                        Contact: founders@hisaab.co.in
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BecomeMerchant;